/**
 * Bluestem Fresh Produce brand pieces - see
 * VIBE/Blustem-company-details/BRAND_GUIDE.md.
 *
 * Bluestem is RSM's mock produce company; every app it fronts is built and
 * sponsored by RSM, so the header carries both marks: the bluestem lockup on
 * the left, "Powered by" + the RSM logo at the right end beside the avatar.
 */

export const BRAND = {
  legalName: 'Bluestem Fresh Produce, Inc.',
  wordmark: 'bluestem',
  sponsor: 'RSM',
  /** Default signed-in user when Cloudflare Access is not in front (employees.csv). */
  persona: { name: 'Nate Holm', title: 'Logistics Manager', site: 'GRP' },
};

/**
 * The mark from bluestem_logo.svg, inlined so it scales crisply and costs no
 * request: a single leaf (RSM Green) on a rising stem (RSM Blue). The viewBox is
 * cropped to the mark; the wordmark is set as live text beside it.
 */
export function BluestemMark({ height = 28 }: { height?: number }) {
  const width = Math.round((height * 214) / 234);
  return (
    <svg width={width} height={height} viewBox="36 14 214 234" aria-hidden="true" focusable="false">
      <path d="M62 232 Q75 150 150 130" stroke="#009CDE" strokeWidth="26" strokeLinecap="round" fill="none" />
      <g transform="translate(155 108) rotate(-45)">
        <path d="M-72 0 C-40 -50 40 -50 72 0 C40 50 -40 50 -72 0 Z" fill="#3F9C35" />
        <path d="M-45 0 L45 0" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" />
      </g>
    </svg>
  );
}

/** Mark + wordmark. On Midnight the wordmark reads white "blue" + green "stem". */
export function BluestemLockup() {
  return (
    <div className="brand" title={BRAND.legalName}>
      <BluestemMark />
      <span className="wordmark" aria-label={BRAND.wordmark}>
        <span className="wm-blue" aria-hidden="true">
          blue
        </span>
        <span className="wm-stem" aria-hidden="true">
          stem
        </span>
      </span>
    </div>
  );
}

/** RSM sponsor mark: white knockout for the Midnight ribbon, 20px tall. */
export function PoweredByRsm() {
  return (
    <span className="sponsor">
      <span className="powered">Powered by</span>
      <img src="/brand/rsmus-logo-white.png" alt={BRAND.sponsor} height={20} />
    </span>
  );
}

/** "ryan.masschelin@example.com" -> "RM", "Nate Holm" -> "NH". */
function initialsOf(name: string): string {
  const local = name.split('@')[0] ?? name;
  const parts = local.split(/[^a-z0-9]+/i).filter(Boolean);
  const first = parts[0] ?? local;
  const second = parts[1];
  const initials = second ? first.charAt(0) + second.charAt(0) : first.slice(0, 2);
  return initials.toUpperCase();
}

/**
 * Avatar at the right end of the ribbon. Shows whoever Cloudflare Access let
 * in; falls back to the demo persona when the site is running unauthenticated.
 */
export function UserAvatar({ user }: { user: string | null | undefined }) {
  const label = user
    ? `Signed in via Cloudflare Access as ${user}`
    : `${BRAND.persona.name} · ${BRAND.persona.title} · ${BRAND.persona.site}`;
  return (
    <span className="avatar" title={label} aria-label={label} role="img">
      {initialsOf(user ?? BRAND.persona.name)}
    </span>
  );
}
